import React from 'react';
import type { TodayAction, TodaySummary } from './summary';
import type { CostSummary } from './costs';
import type { DrawerActionType } from './workflowBoard';
import {
  ActionsCard,
  AttentionCard,
  ChangeOrdersCard,
  CollectionsChart,
  ConstructionCard,
  CostCard,
  HeroCard,
  InventoryCard,
  LoansCard,
  PaymentsCard,
  StatCard,
  StatutoryCard,
  UnitsCard,
  UpcomingCard,
  WorkflowCard,
  inr,
} from './cards';

export type CardContext = {
  s: TodaySummary;
  costs: CostSummary;
  greeting: string;
  recordReceipt: () => void;
  go: (path: string) => void;
  openUnit: (unitId: string) => void;
  openStep: (unitId: string, action: DrawerActionType | null) => void;
  onAction: (a: TodayAction) => void;
};

type CardDef = {
  id: string;
  name: string;
  layout: { x: number; y: number; w: number; h: number; minW?: number; minH?: number };
  render: (ctx: CardContext, index: number) => React.ReactNode;
};

// 12 columns; one row unit is 20px plus the 14px gap.
export const CARD_DEFS: CardDef[] = [
  {
    id: 'hero',
    name: 'Collection summary',
    layout: { x: 0, y: 0, w: 6, h: 16, minW: 4, minH: 9 },
    render: ({ s, greeting, go, recordReceipt }) => <HeroCard s={s} greeting={greeting} onRecord={recordReceipt} onLedger={() => go('/office/reports')} />,
  },
  {
    id: 'received',
    name: 'Received',
    layout: { x: 6, y: 0, w: 3, h: 8, minW: 3, minH: 7 },
    render: ({ s, go }, i) => (
      <StatCard index={i} label="Received" icon="wallet" tone="acc" value={s.received} chip={<span className="ot-chip c-good">+{inr(s.receivedThisMonth)} this month</span>}
        fill={s.collectionPct} meter="m-acc" rows={[['Last month', inr(s.receivedPrevMonth)], ['Collection', `${s.collectionPct}%`]]} onOpen={() => go('/office/reports')} />
    ),
  },
  {
    id: 'pending',
    name: 'Pending',
    layout: { x: 9, y: 0, w: 3, h: 8, minW: 3, minH: 7 },
    render: ({ s, go }, i) => (
      <StatCard index={i} label="Pending" icon="coin" tone="mute" value={s.pending} chip={<span className="ot-chip c-mute">{s.unitRows.filter((r) => r.pending > 0).length} units with balance</span>}
        fill={s.pending > 0 ? (s.dues.due30 / s.pending) * 100 : 0} meter="m-ink" rows={[['Due next 7 days', inr(s.dues.due7)], ['Due next 30 days', inr(s.dues.due30)]]} onOpen={() => go('/office/reports')} />
    ),
  },
  {
    id: 'overdue',
    name: 'Overdue',
    layout: { x: 6, y: 8, w: 3, h: 8, minW: 3, minH: 7 },
    render: ({ s }, i) => (
      <StatCard index={i} label="Overdue" icon="alert" tone="bad" value={s.dues.overdue} chip={<span className={`ot-chip ${s.dues.overdueUnits ? 'c-bad' : 'c-good'}`}>{s.dues.overdueUnits} units</span>}
        fill={s.pending > 0 ? (s.dues.overdue / s.pending) * 100 : 0} meter="m-bad" rows={[['Oldest', s.dues.oldestDays ? `${s.dues.oldestDays} days` : '-'], ['No payment plan', `${s.dues.noPlanUnits} units`]]} />
    ),
  },
  {
    id: 'gst',
    name: 'GST balance',
    layout: { x: 9, y: 8, w: 3, h: 8, minW: 3, minH: 7 },
    render: ({ s, go }, i) => (
      <StatCard index={i} label="GST balance" icon="gst" tone="warn" value={s.gst.balance} chip={<span className="ot-chip c-warn">{s.gst.unitsPending} units pending</span>}
        fill={s.gst.due > 0 ? (s.gst.collected / s.gst.due) * 100 : 0} meter="m-warn" rows={[['Billable on booked units', inr(s.gst.due)], ['Collected from buyers', inr(s.gst.collected)]]} onOpen={() => go('/office/reports')} />
    ),
  },
  {
    id: 'bookedUnits',
    name: 'Booked units',
    layout: { x: 0, y: 108, w: 3, h: 8, minW: 3, minH: 7 },
    render: ({ s, go }, i) => (
      <StatCard index={i} label="Booked units" icon="home" tone="good" value={s.bookedUnits} valueFormat={(v) => String(Math.round(v))} chip={<span className="ot-chip c-mute">{s.inventory.available + s.inventory.form} available</span>}
        fill={s.inventory.total ? (s.bookedUnits / s.inventory.total) * 100 : 0} meter="m-good" rows={[['Agreement done', String(s.inventory.agreement + s.inventory.registered)], ['Sale deed done', String(s.inventory.registered)]]} onOpen={() => go('/office/units')} />
    ),
  },
  { id: 'chart', name: 'Collections chart', layout: { x: 0, y: 16, w: 8, h: 11, minW: 5, minH: 9 }, render: ({ s, go }, i) => <CollectionsChart s={s} index={i} onOpen={() => go('/office/reports')} /> },
  { id: 'actions', name: "Today's actions", layout: { x: 8, y: 16, w: 4, h: 11, minW: 3, minH: 6 }, render: ({ s, onAction }, i) => <ActionsCard actions={s.actions} index={i} onAction={onAction} /> },
  { id: 'units', name: 'Units', layout: { x: 0, y: 27, w: 8, h: 18, minW: 5, minH: 9 }, render: ({ s, openUnit, go }, i) => <UnitsCard rows={s.unitRows} index={i} onOpenUnit={openUnit} onOpenAll={() => go('/office/units')} /> },
  { id: 'upcoming', name: 'Upcoming', layout: { x: 8, y: 27, w: 4, h: 9, minW: 3, minH: 6 }, render: ({ s, openUnit }, i) => <UpcomingCard s={s} index={i} onOpenUnit={openUnit} /> },
  { id: 'attention', name: 'Needs attention', layout: { x: 8, y: 36, w: 4, h: 9, minW: 3, minH: 6 }, render: ({ s, go, openUnit }, i) => <AttentionCard s={s} index={i} onOpenUnits={() => go('/office/units')} onOpenUnit={openUnit} /> },
  { id: 'workflow', name: 'Booking workflow', layout: { x: 0, y: 45, w: 8, h: 10, minW: 5, minH: 7 }, render: ({ s, openStep }, i) => <WorkflowCard s={s} index={i} onStep={openStep} /> },
  { id: 'loans', name: 'Home loans', layout: { x: 8, y: 45, w: 4, h: 10, minW: 3, minH: 6 }, render: ({ s }, i) => <LoansCard s={s} index={i} /> },
  { id: 'payments', name: 'Payments', layout: { x: 0, y: 55, w: 8, h: 16, minW: 5, minH: 8 }, render: ({ s, openUnit }, i) => <PaymentsCard s={s} index={i} onOpenUnit={openUnit} /> },
  { id: 'changeOrders', name: 'Change orders', layout: { x: 8, y: 55, w: 4, h: 8, minW: 3, minH: 6 }, render: ({ s, go }, i) => <ChangeOrdersCard s={s} index={i} onOpen={() => go('/office/change-orders')} /> },
  { id: 'statutory', name: 'GST and RERA', layout: { x: 8, y: 63, w: 4, h: 8, minW: 3, minH: 6 }, render: ({ s }, i) => <StatutoryCard s={s} index={i} /> },
  { id: 'cost', name: 'Construction cost', layout: { x: 0, y: 71, w: 6, h: 16, minW: 4, minH: 8 }, render: ({ s, costs, go }, i) => <CostCard s={s} costs={costs} index={i} onOpenBills={() => go('/office/material-orders')} onOpenEstimate={() => go('/office/estimation')} /> },
  { id: 'inventory', name: 'Inventory', layout: { x: 6, y: 71, w: 6, h: 8, minW: 4, minH: 6 }, render: ({ s, go }, i) => <InventoryCard s={s} index={i} onOpen={() => go('/office/units')} /> },
  { id: 'construction', name: 'Construction progress', layout: { x: 6, y: 79, w: 6, h: 8, minW: 3, minH: 6 }, render: (_ctx, i) => <ConstructionCard index={i} /> },
];

/** Cards that start hidden; users add them from the Customize tray. */
export const DEFAULT_HIDDEN = ['bookedUnits'];

export const CARD_BY_ID = new Map(CARD_DEFS.map((c) => [c.id, c]));
